import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Stars from "@/components/Stars";

function timeAgo(iso) {
  try {
    const then = new Date(iso).getTime();
    const s = Math.floor((Date.now() - then) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
    return `${Math.floor(s / 86400)} d ago`;
  } catch { return ""; }
}

export default function ProductReviews({ slug }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ count: 0, average: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/products/${slug}/reviews`)
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, average: 0, items: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Please log in to leave a review.");
      nav(`/login?next=/product/${slug}`);
      return;
    }
    if (!comment.trim()) { toast.error("Please write a short review."); return; }
    setBusy(true);
    try {
      await api.post(`/products/${slug}/reviews`, { rating, title: title.trim() || null, comment: comment.trim() });
      toast.success("Thanks for your review!");
      setTitle(""); setComment(""); setRating(5);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review.");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="product-reviews" className="mt-16 border-t border-stone-200 pt-12">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Customer Reviews</p>
          <div className="flex items-center gap-3 mt-3">
            <Stars value={data.average} size={20} testid="reviews-average-stars" />
            <span data-testid="reviews-average" className="font-serif text-2xl">{data.average.toFixed(1)}</span>
            <span className="text-sm text-stone-500">({data.count} review{data.count === 1 ? "" : "s"})</span>
          </div>
        </div>
      </div>

      {/* Submission form */}
      <form onSubmit={submit} className="mt-8 p-6 bg-stone-100/60 border border-stone-200" data-testid="review-form">
        <p className="text-xs tracking-widest uppercase text-[#8B5A2B]">Share your experience</p>
        <div className="mt-3 flex items-center gap-4">
          <span className="text-sm text-stone-600">Your rating</span>
          <Stars value={rating} size={22} onChange={setRating} testid="review-rating-picker" />
        </div>
        <input
          data-testid="review-title-input"
          type="text"
          placeholder="Give your review a short title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-4 w-full"
          maxLength={80}
        />
        <textarea
          data-testid="review-comment-input"
          placeholder="Tell other customers what you loved about this piece..."
          rows={3}
          required
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="mt-3 w-full"
          maxLength={800}
        />
        <div className="mt-4 flex items-center gap-3">
          <button
            data-testid="review-submit-btn"
            type="submit"
            disabled={busy}
            className="btn-primary disabled:opacity-60"
          >
            {busy ? "Posting…" : (user ? "Post Review" : "Log in to Post")}
          </button>
          {!user && <span className="text-xs text-stone-500">You'll be redirected to log in.</span>}
        </div>
      </form>

      {/* Review list */}
      <div className="mt-8">
        {loading ? (
          <p className="text-sm text-stone-500">Loading reviews…</p>
        ) : data.items.length === 0 ? (
          <p data-testid="reviews-empty" className="text-sm text-stone-500 italic">No reviews yet. Be the first to share your experience.</p>
        ) : (
          <ul className="space-y-6">
            {data.items.map((r) => (
              <li key={r.id} data-testid={`review-${r.id}`} className="border-b border-stone-200 pb-6 last:border-b-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <Stars value={r.rating} size={13} />
                  <span className="font-medium text-stone-800 text-sm">{r.user_name}</span>
                  {r.verified_purchase && (
                    <span data-testid={`review-verified-${r.id}`} className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-[#5c7a3a]">
                      <CheckCircle2 size={12} /> Verified Buyer
                    </span>
                  )}
                  <span className="ml-auto text-xs text-stone-400">{timeAgo(r.created_at)}</span>
                </div>
                {r.title && <p className="mt-2 font-serif text-lg text-stone-900">{r.title}</p>}
                <p className="mt-2 text-sm text-stone-600 leading-relaxed whitespace-pre-line">{r.comment}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
